import { ref } from 'vue';
import { airflow, type SearchParams } from '@aviata/platform-client';
import { hashCodeLib } from 'shared';

import {
  nearestSearchesApi,
  nearestSearchesLib,
} from '@/entities/nearest-searches';

import { sentryModel } from '@/shared/model';

import { DEFAULT_RT_SEARCHES_RANGE as defaultDatesRange } from '../config';
import type { MappedRoundTripNearestSearch } from '../types';

export const useGetRoundTripNearestSearches = (searchParams: SearchParams) => {
  const departureNearestSearches = ref<MappedRoundTripNearestSearch[]>([]);
  const arrivalNearestSearches = ref<MappedRoundTripNearestSearch[]>([]);
  const selectedDates = ref<string[]>(['', '']);
  const isLoading = ref<boolean>(false);

  const init = (hashCode: string) => {
    isLoading.value = true;

    const { itineraries } = hashCodeLib.getSearchParams(hashCode);

    selectedDates.value = itineraries.map(it => it.date);

    const newQueries = itineraries.map(itinerary => {
      const selectedSearchDate = itinerary.date;
      const requestDateRange = nearestSearchesLib.getDateRangeForGetRequest(
        selectedSearchDate,
        defaultDatesRange,
      );

      const nextQuery = airflow.utils.buildQuery({
        ...searchParams,
        itineraries: [itinerary],
      });
      return nearestSearchesApi.getNearestSearches(nextQuery, requestDateRange);
    });

    const queryResponses = Promise.all(newQueries);

    return queryResponses
      .then(([departureSearches, arrivalSearches]) => {
        departureNearestSearches.value = nearestSearchesLib.mapSearches(
          departureSearches,
          itineraries[0].date,
          defaultDatesRange,
        );
        arrivalNearestSearches.value = nearestSearchesLib.mapSearches(
          arrivalSearches,
          itineraries[1].date,
          defaultDatesRange,
        );
      })
      .catch(e => {
        sentryModel.captureException(
          sentryModel.getReadableError('ERR_TO_FETCH_NEAREST_SEARCHES_RT', e),
          {
            extra: {
              scope: e.response?.data?.errors[0]?.scope,
              message: e.response?.data?.errors[0]?.message,
            },
          },
        );
        throw e;
      })
      .finally(() => (isLoading.value = false));
  };

  return {
    departureNearestSearches,
    arrivalNearestSearches,
    selectedDates,
    isLoading,
    init,
  };
};
